import api from './api';
import { nullableUuid } from '@/utils/uuid';

export type RomaneioStatus = 'ABERTO' | 'CARREGADO' | 'EM_TRANSITO' | 'ENTREGUE' | 'CANCELADO';

export interface RomaneioItem {
  id?: string;
  produto_id: string;
  produto_codigo?: string | null;
  produto_descricao?: string | null;
  lote_id?: string | null;
  lote_numero?: string | null;
  quantidade: number;
  ordem_producao_id?: string | null;
  op_numero?: string | null;
  observacoes?: string | null;
}

export interface Romaneio {
  id: string;
  numero: string;
  status: RomaneioStatus;
  data_emissao: string;
  data_carregamento?: string | null;
  data_entrega?: string | null;
  destinatario_nome?: string | null;
  destinatario_cnpj?: string | null;
  destinatario_endereco?: string | null;
  transportadora?: string | null;
  veiculo_id?: string | null;
  veiculo_placa?: string | null;
  motorista?: string | null;
  responsavel_emissao_id?: string | null;
  responsavel_carregamento_id?: string | null;
  observacoes?: string | null;
  itens: RomaneioItem[];
}

export interface RomaneioInput {
  numero: string;
  veiculo_id?: string | null;
  veiculo_placa?: string | null;
  motorista?: string | null;
  destinatario_nome?: string | null;
  destinatario_cnpj?: string | null;
  destinatario_endereco?: string | null;
  transportadora?: string | null;
  responsavel_emissao_id?: string | null;
  observacoes?: string | null;
  itens: RomaneioItem[];
}

const ENDPOINT = '/industrializacao/romaneio';

const romaneioService = {
  async listar(filters: { status?: string; veiculo?: string; search?: string; motorista?: string } = {}): Promise<Romaneio[]> {
    const response = await api.get<Romaneio[]>(ENDPOINT, { params: filters });
    return response.data;
  },
  async buscarPorId(id: string): Promise<Romaneio> {
    const response = await api.get<Romaneio>(`${ENDPOINT}/${id}`);
    return response.data;
  },
  async criar(data: RomaneioInput): Promise<Romaneio> {
    const response = await api.post<Romaneio>(ENDPOINT, {
      ...data,
      veiculo_id: nullableUuid(data.veiculo_id),
      responsavel_emissao_id: nullableUuid(data.responsavel_emissao_id),
      itens: data.itens.map((item) => ({
        ...item,
        produto_id: nullableUuid(item.produto_id) || '',
        lote_id: nullableUuid(item.lote_id),
        ordem_producao_id: nullableUuid(item.ordem_producao_id),
      })),
    });
    return response.data;
  },
  async excluir(id: string): Promise<void> {
    await api.delete(`${ENDPOINT}/${id}`);
  },
  /** Marca como CARREGADO (exige responsavel_carregamento_id) - somente de ABERTO */
  async carregar(id: string, responsavelCarregamentoId: string): Promise<Romaneio> {
    const response = await api.post<Romaneio>(`${ENDPOINT}/${id}/carregar`, { responsavel_carregamento_id: nullableUuid(responsavelCarregamentoId) });
    return response.data;
  },
  /** Marca como EM_TRANSITO - somente de CARREGADO */
  async expedir(id: string): Promise<Romaneio> {
    const response = await api.post<Romaneio>(`${ENDPOINT}/${id}/expedir`);
    return response.data;
  },
  /** Marca como ENTREGUE - somente de EM_TRANSITO */
  async entregar(id: string): Promise<Romaneio> {
    const response = await api.post<Romaneio>(`${ENDPOINT}/${id}/entregar`);
    return response.data;
  },
  /** Marca como CANCELADO - de ABERTO/CARREGADO/EM_TRANSITO (nao pode se ja ENTREGUE) */
  async cancelar(id: string): Promise<Romaneio> {
    const response = await api.post<Romaneio>(`${ENDPOINT}/${id}/cancelar`);
    return response.data;
  },
  /** Backend ainda nao implementa geracao de PDF (retorna 501) - manter chamada real para refletir esse estado */
  async imprimir(id: string): Promise<{ error: string }> {
    const response = await api.get<{ error: string }>(`${ENDPOINT}/${id}/imprimir`);
    return response.data;
  },
};

export default romaneioService;
